import type { PwaInstallPlatform, PwaInstallResult, PwaInstallState } from '../types';

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{
      outcome: 'accepted' | 'dismissed';
      platform: string;
    }>;
  }

  interface Navigator {
    standalone?: boolean;
  }
}

type Listener = () => void;

const listeners = new Set<Listener>();

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let started = false;

let state: PwaInstallState = {
  isStandalone: false,
  canPromptInstall: false,
  platform: 'other',
};

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const updateState = (partialState: Partial<PwaInstallState>) => {
  state = {
    ...state,
    ...partialState,
  };

  notifyListeners();
};

const detectPlatform = (): PwaInstallPlatform => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIpad = /ipad/.test(userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  if (/iphone|ipod/.test(userAgent) || isIpad) {
    return 'ios';
  }

  if (/android/.test(userAgent)) {
    return 'android';
  }

  const isSafari = /safari/.test(userAgent) && !/chrome|chromium|crios|edg|opr/.test(userAgent);
  if (isSafari && /mac/.test(userAgent)) {
    return 'safari-desktop';
  }

  if (/chrome|chromium|crios|edg|opr/.test(userAgent) && !/android/.test(userAgent)) {
    return 'chromium-desktop';
  }

  return 'other';
};

const computeStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true ||
  document.referrer.includes('android-app://');

const updateStandaloneState = () => {
  updateState({
    isStandalone: computeStandalone(),
  });
};

const addMediaQueryListener = (mediaQuery: MediaQueryList, handler: () => void) => {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handler);
    return;
  }

  const legacyMediaQuery = mediaQuery as MediaQueryList & {
    addListener?: (listener: () => void) => void;
  };
  legacyMediaQuery.addListener?.(handler);
};

export const initializePwa = () => {
  if (started || typeof window === 'undefined') {
    return;
  }

  started = true;
  updateState({
    platform: detectPlatform(),
  });
  updateStandaloneState();

  const standaloneQuery = window.matchMedia('(display-mode: standalone)');
  addMediaQueryListener(standaloneQuery, updateStandaloneState);

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    const installEvent = event as BeforeInstallPromptEvent;
    installEvent.preventDefault();
    deferredPrompt = installEvent;
    updateState({
      canPromptInstall: true,
    });
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    updateState({
      canPromptInstall: false,
      isStandalone: true,
    });
  });

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.warn('No se pudo registrar el service worker de Juris.', error);
      });
    });
  }
};

export const subscribeToPwaInstall = (listener: Listener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const getPwaInstallState = () => state;

export const promptPwaInstall = async (): Promise<PwaInstallResult> => {
  if (!deferredPrompt) {
    return 'unavailable';
  }

  const installEvent = deferredPrompt;
  deferredPrompt = null;
  updateState({
    canPromptInstall: false,
  });

  try {
    await installEvent.prompt();
    const choiceResult = await installEvent.userChoice;
    return choiceResult.outcome;
  } catch (error) {
    console.warn('No se pudo abrir el prompt nativo de instalacion PWA.', error);
    return 'unavailable';
  }
};
